import { gsap } from 'gsap';

// Pulse & Assemble — the single search-bar transition for every prompter
// result (replaces the ported Angular dual-flow prompter-animation):
//   1. the search bar pulses brand-red (PULSE_CLASS; keyframes live next to
//      .search-container in src/styles/styles.scss),
//   2. the typed words pop out left-to-right,
//   3. onSwap() renders the canonical tag HTML (NavBar's showNewPromptOnDiv),
//   4. the new spans spring in one-by-one, each from a random vertical
//      direction.
// Tag markup, ids, and click semantics are owned entirely by the swap
// callback — this module never builds or edits tag HTML.

export const PULSE_CLASS = 'prompter-pulse';
// Single source of truth for the pulse duration — fed to CSS via the
// --prompter-pulse-duration custom property set in start() below.
const PULSE_TOTAL_MS = 1200;

export class PrompterTransition {
  private animating = false;
  private pendingSwap: (() => void) | null = null;
  private activeTweens: Array<{ kill: () => void }> = [];
  private pulseEl: HTMLElement | null = null;
  private pulseTimer: number | undefined;

  start(
    editableDiv: HTMLDivElement,
    barEl: HTMLElement,
    onSwap: () => void,
    onComplete?: () => void
  ): void {
    this.stop(); // a re-trigger kills the previous run (and flushes its swap)
    this.animating = true;

    // The swap must run exactly once per start(), even if stop() interrupts.
    // The callback re-renders DOM and touches stores — if it throws, swallow
    // and log so the transition never sticks at animating=true (GSAP silently
    // eats exceptions thrown inside its onComplete callbacks).
    this.pendingSwap = () => {
      this.pendingSwap = null;
      try {
        onSwap();
      } catch (err) {
        console.error('PrompterTransition: swap callback failed', err);
      }
    };

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.pendingSwap();
      this.animating = false;
      onComplete?.();
      return;
    }

    this.pulseEl = barEl;
    barEl.style.setProperty('--prompter-pulse-duration', `${PULSE_TOTAL_MS}ms`);
    // Force a style recalc between stop()'s class removal and this add —
    // without it, a rapid re-trigger in the same task does not restart the
    // CSS pulse animation.
    void barEl.offsetWidth;
    barEl.classList.add(PULSE_CLASS);
    this.pulseTimer = window.setTimeout(() => {
      barEl.classList.remove(PULSE_CLASS);
    }, PULSE_TOTAL_MS);

    const finish = () => {
      this.animating = false;
      onComplete?.();
    };

    const assemble = () => {
      this.pendingSwap?.();
      // Query AFTER the swap — these spans are the canonical tag elements
      // just rendered by the caller, not the old word spans.
      const spans = editableDiv.querySelectorAll('span');
      if (spans.length === 0) {
        finish();
        return;
      }
      this.activeTweens.push(
        gsap.from(spans, {
          opacity: 0,
          scale: 0.5,
          y: () => (Math.random() < 0.5 ? -22 : 22),
          duration: 0.5,
          stagger: 0.12,
          ease: 'back.out(2.2)',
          clearProps: 'all',
          onComplete: finish,
        })
      );
    };

    if (!readText(editableDiv).trim()) {
      assemble();
      return;
    }
    splitIntoWordSpans(editableDiv);
    const words = editableDiv.querySelectorAll('span');
    this.activeTweens.push(
      gsap.to(words, {
        opacity: 0,
        scale: 0.4,
        duration: 0.25,
        stagger: 0.06,
        ease: 'power1.in',
        onComplete: assemble,
      })
    );
  }

  isAnimating(): boolean {
    return this.animating;
  }

  // Kills tweens but ALWAYS flushes a pending swap — the bar must never be
  // left with half-faded words; the canonical repaint is the invariant.
  stop(): void {
    this.activeTweens.forEach((tween) => tween.kill());
    this.activeTweens = [];
    window.clearTimeout(this.pulseTimer);
    this.pulseEl?.classList.remove(PULSE_CLASS);
    this.pulseEl = null;
    this.pendingSwap?.();
    this.animating = false;
  }
}

// The one piece of the old animation worth keeping: split the typed text into
// word spans so each word can pop out individually.
export function splitIntoWordSpans(div: HTMLDivElement): void {
  const words = readText(div).split(/\s+/).filter((word) => word !== '');
  div.innerHTML = '';
  words.forEach((word) => {
    const span = document.createElement('span');
    span.textContent = word + ' ';
    div.appendChild(span);
  });
}

// jsdom does not reliably implement innerText; browsers always do.
function readText(div: HTMLDivElement): string {
  return div.innerText ?? div.textContent ?? '';
}
